import { useEffect } from 'react';
import { stagePlayer } from './stagePlayer.js';
import { getTuning } from './audioTuning.js';
import { LOBBY_MUSIC, MODE_PICK_LINES } from '../assets/audioManifest.js';

/**
 * useMenuMusic — score the front door.
 *
 * The lobby music used to start only once the room's LOBBY soundscape fired,
 * which meant the game-select screen (the FIRST thing anyone sees in the
 * Activity) sat in silence. This starts the same track, at the same tuned gain
 * and role, as soon as the screen mounts — so the pick, the lobby, and the deal
 * are one unbroken bed (stagePlayer's setAmbient de-dupes an identical set, so
 * the LOBBY phase does NOT restart it).
 *
 * Audio may be locked at mount (a cold browser tab). tryUnlock() takes it if the
 * launch gesture carried through (the usual case inside Discord); otherwise the
 * request waits on onUnlocked and lands the moment the first click unlocks audio
 * (App's global click handler). Deliberately does NOT stop on unmount — the game
 * that follows owns the bed from there.
 */
export function useMenuMusic(): void {
  useEffect(() => {
    stagePlayer.tryUnlock();
    return stagePlayer.onUnlocked(() => {
      stagePlayer.setAmbient([
        { url: LOBBY_MUSIC, gain: getTuning().gains.lobby, role: 'lobby' },
      ]);
    });
  }, []);
}

/**
 * Play the Ringmaster's reaction to a mode pick ("Classic, bold of you — now
 * gather your table"), chosen at random from that mode's pre-voiced lines.
 *
 * Goes through the narration queue rather than the SFX bus so it ducks the menu
 * music exactly like an in-game beat. Unknown game id (or a bank that never
 * shipped) → silence, never an error. The line is voice-only: the tap navigates
 * to the lobby, which has no caption band to carry text.
 */
export function playModePickLine(gameId: string): void {
  const lines = MODE_PICK_LINES[gameId];
  if (!lines || lines.length === 0) return;
  const url = lines[Math.floor(Math.random() * lines.length)]!;
  // The pick tap is itself a valid unlock gesture, and it may be the FIRST one
  // (a table that picks before the menu music has managed to start). Without
  // this the queue would drop the line as "audio not ready" and say nothing.
  stagePlayer.unlock();
  // enqueue() treats a leading "/" as a ready-made path (Vite asset URLs are),
  // so this needs no server audio id.
  stagePlayer.enqueue(url, '');
}
